<script setup lang="ts">
type Props = {
  // Classe extra no overlay do modal de resgate. Existe por causa do Clube
  // Fidelidade: lá esta lista vive DENTRO de um modal de z-9994, e o overlay do
  // BaseModal é z-1000 — sem subir o z-index dele, o "Resgatar" abriria ATRÁS do
  // painel. Na rota /user/rewards fica vazia e nada muda.
  modalClass?: string;
};

const { modalClass = '' } = defineProps<Props>();

// Repassado do modal de resgate: quem embute esta lista num painel próprio
// (LoyaltyHubRewardsSheet) escuta pra se fechar antes da navegação do "Jogar
// Agora". Na rota /user/rewards ninguém escuta e nada muda.
const emit = defineEmits<{ play: [] }>();

const { isRedeemableRewardsEnabled } = useFeatureFlagsJson();

const activeTab = ref<'available' | 'finisheds'>(
  isRedeemableRewardsEnabled.value ? 'available' : 'finisheds',
);

const activeFilter = ref<RewardType | 'all'>('all');
const isModalVisible = ref(false);
const isLoadingRedeem = ref(false);
const selectedReward = ref<Reward | null>(null);

const {
  fetchRewardsFromApi,
  rewardsFromApi,
  isLoadingRewards,
  rewrdsPagination,
} = useRewards();

const availableRewards = computed(() => {
  return rewardsFromApi.value.available;
});

const finishedsRewards = computed(() => {
  return rewardsFromApi.value.finisheds;
});

function openModal(reward: Reward) {
  selectedReward.value = reward;
  isModalVisible.value = true;
}

function goToTopOfTable() {
  const tableElement = document.querySelector(
    '.identifierToGoTop',
  ) as HTMLElement;

  const offsetTop = tableElement ? tableElement.offsetTop : 0;

  scrollToTop(offsetTop);
}

function handleChangePage(options: { page: number }) {
  fetchRewardsFromApi(activeTab.value, activeFilter.value, options.page);
  goToTopOfTable();
}

onMounted(() => {
  if (isRedeemableRewardsEnabled.value) {
    if (!availableRewards.value || !availableRewards.value.length) {
      fetchRewardsFromApi('available', 'all', 1);
    }
  } else if (!finishedsRewards.value || !finishedsRewards.value.length) {
    fetchRewardsFromApi('finisheds', 'all', 1);
  }
});
</script>

<template>
  <UserBox>
    <UserRewardsTabs
      v-model="activeTab"
      :available-rewards="availableRewards"
      class="identifierToGoTop"
      :current-type="activeFilter"
      :finisheds-rewards="finishedsRewards"
      :is-redeemable-rewards-enabled="isRedeemableRewardsEnabled"
    />

    <UserRewardsFilters
      v-model="activeFilter"
      :current-status="activeTab"
    />

    <div :class="$style.rewardsContentContainer">
      <template v-if="activeTab === 'available'">
        <div
          v-if="isLoadingRewards"
          :class="$style.rewardsLoading"
        >
          <BaseCommonSkeleton
            v-for="i in 8"
            :key="i"
            :class="$style.skeleton"
          />
        </div>

        <div
          v-else-if="availableRewards.length > 0"
          :class="$style.rewardsContent"
        >
          <UserRewardsItem
            v-for="reward in availableRewards"
            :key="reward.id"
            :reward="reward"
            @redeem="openModal(reward)"
          />

          <UserWalletPagination
            v-if="rewrdsPagination.available.last_page > 1"
            :end="rewrdsPagination.available.to"
            :last-page="rewrdsPagination.available.last_page"
            :page="rewrdsPagination.available.current_page"
            :per-page="rewrdsPagination.available.per_page"
            :start="rewrdsPagination.available.from"
            :total="rewrdsPagination.available.total"
            @change-page="handleChangePage"
          />
        </div>

        <UserRewardsEmpty
          v-else
          title="Nenhum prêmio disponível"
        />
      </template>

      <template v-else-if="activeTab === 'finisheds'">
        <div
          v-if="isLoadingRewards"
          :class="$style.rewardsLoading"
        >
          <BaseCommonSkeleton
            v-for="i in 8"
            :key="i"
            :class="$style.skeleton"
          />
        </div>

        <div
          v-else-if="finishedsRewards.length > 0"
          :class="$style.rewardsContent"
        >
          <UserRewardsItem
            v-for="reward in finishedsRewards"
            :key="reward.id"
            :button-visible="false"
            :reward="reward"
          />

          <UserWalletPagination
            v-if="rewrdsPagination.finisheds.last_page > 1"
            :end="rewrdsPagination.finisheds.to"
            :last-page="rewrdsPagination.finisheds.last_page"
            :page="rewrdsPagination.finisheds.current_page"
            :per-page="rewrdsPagination.finisheds.per_page"
            :start="rewrdsPagination.finisheds.from"
            :total="rewrdsPagination.finisheds.total"
            @change-page="handleChangePage"
          />
        </div>

        <UserRewardsEmpty
          v-else
          title="Nenhum prêmio resgatado"
        />
      </template>
    </div>
  </UserBox>

  <BaseModal
    v-model="isModalVisible"
    :custom-class-modal="modalClass"
    width="560"
  >
    <UserRewardsRedeem
      v-model="isModalVisible"
      :is-loading-redeem="isLoadingRedeem"
      :selected-reward="selectedReward"
      @play="emit('play')"
    />
  </BaseModal>
</template>

<style module lang="postcss">
.rewardsContentContainer {
  @apply pt-4 md:pt-5;

  .rewardsLoading {
    @apply flex flex-col gap-3 w-full;

    .skeleton {
      @apply w-full h-20 rounded-lg bg-texts/5 border border-solid border-texts/10;
    }
  }

  .rewardsContent {
    @apply flex flex-col gap-3;
  }
}
</style>
